/* eslint-disable prettier/prettier */
/* eslint-disable import/order */
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
} from "@mui/material";
import axios from "axios";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import { IConnectivityOption } from "../../interfaces/IConnectivityOption";
import { IVehicle } from "../../interfaces/IVehicle";
import { CONFIG } from "../../services/config";
import ModalConfirmation from "../MyModal/modalConfirmation";
import VersionVehicle from "./versionVehicle";

const INITIAL_YEARS = ["All"];
const PAGINATION_INCREMENT = 10;
const LAZY_LOAD_INCREMENT = 5;
const LOADING_DELAY = 500;
const ERROR_DISPLAY_DURATION = 5000;

interface ModelVehicleProps {
  readonly errors: any;
  readonly editVehicle: boolean;
  readonly watchScroll: number;
  readonly data: IVehicle;
}

export default function ModelVehicle({ 
  errors, 
  editVehicle,
  watchScroll,
  data 
}: ModelVehicleProps) {

  const { t } = useTranslation();

  const addButtonRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [loadingStates, setLoadingStates] = useState({
    main: false,
    copyEnvironment: false,
    addButton: false,
    lazyLoading: false,
  });

  const [lazyLoadState, setLazyLoadState] = useState({
    visibleCount: LAZY_LOAD_INCREMENT,
    hasMore: true,
  });

  const [yearFilter, setYearFilter] = useState({
    isEnabled: true,
    selectedYear: "",
    availableYears: INITIAL_YEARS,
    noResultsFound: false,
  });

  const [modelStates, setModelStates] = useState({
    isNewModel: false,
    newVersionCount: 0,
    pageSize: LAZY_LOAD_INCREMENT,
    childrenLength: 0,
    modelLength: 0,
  });

  const [deleteStates, setDeleteStates] = useState({
    showConfirmDialog: false,
    targetVersionIndex: undefined as number | undefined,
  });

  const [copyStates, setCopyStates] = useState({
    isEnabled: false,
    environment: "",
    country: "",
    selectedVersions: [] as number[],
  });

  const [vehicleData, setVehicleData] = useState<IVehicle>(
    data ?? { id: 0, name: "", teaserMediaId: "", extension: "", backgroundColor: "", versions: [] },
  );
  const [connectivityOptions, setConnectivityOptions] = useState<IConnectivityOption[]>([]);

  const { 
    user,
    tokenHeaders, 
    user: { countries },
    country: { countryCode },
  } = useContext(AuthContext);

  const updateLoadingState = (
    key: keyof typeof loadingStates,
    value: boolean,
  ) => {
    setLoadingStates((prev) => ({ ...prev, [key]: value }));
  };

  const updateYearFilter = (updates: Partial<typeof yearFilter>) => {
    setYearFilter((prev) => ({ ...prev, ...updates }));
  };

  const updateModelStates = (updates: Partial<typeof modelStates>) => {
    setModelStates((prev) => ({ ...prev, ...updates }));
  };

  const updateDeleteStates = (updates: Partial<typeof deleteStates>) => {
    setDeleteStates((prev) => ({ ...prev, ...updates }));
  };

  const updateCopyStates = (updates: Partial<typeof copyStates>) => {
    setCopyStates((prev) => ({ ...prev, ...updates }));
  };

  const updateLazyLoadState = (updates: Partial<typeof lazyLoadState>) => {
    setLazyLoadState((prev) => ({ ...prev, ...updates }));
  };

  const createNewVersion = () => ({
    id: 0,
    name: "",
    modelCode: "",
    year: 0,
    vwPlay: "",
    isCognitive: "",
    isCarNetEnabled: false,
    isPatagoniaEnabled: false,
    isElectric: false,
    hasOtto: false,
    hasSmartManual: false,
    connectivity: "",
    version: [],
  });

  const showErrorMessage = () => {
    const errorElement = document.querySelector(
      "#requiredVersion",
    ) as HTMLDivElement;
    if (errorElement) {
      errorElement.style.display = "block";
      setTimeout(() => {
        errorElement.style.display = "none";
      }, ERROR_DISPLAY_DURATION);
    }
  };

  const handleEnvironmentChange = (event: SelectChangeEvent) => {
    updateCopyStates({ environment: event.target.value });
  };

  const toggleYearVisibility = (years: string[], displayStyle: string) => {
    years.forEach((year) => {
      const yearElements = document.getElementsByClassName(`year${year}`);
      Array.from(yearElements).forEach((element) => {
        (element as HTMLElement).style.display = displayStyle;
      });
    });
  };

  const showAllYearVersions = (displayStyle: string) => {
    setTimeout(() => {
      toggleYearVisibility(yearFilter.availableYears, displayStyle);
    }, LOADING_DELAY);
  };

  const showSpecificYearVersions = (
    selectedYear: string,
    displayStyle: string,
  ) => {
    setTimeout(() => {
      const yearElements = document.getElementsByClassName(selectedYear);
      const hasResults = yearElements.length > 0;

      updateYearFilter({
        noResultsFound: !hasResults && selectedYear !== "yearAll",
      });

      toggleYearVisibility(yearFilter.availableYears, "none");

      Array.from(yearElements).forEach((element) => {
        (element as HTMLElement).style.display = "block";
      });
    }, LOADING_DELAY);
  };

  const handleYearSelection = (selectedYear: string) => {
    updateLoadingState("main", true);
    updateYearFilter({ selectedYear });

    updateLazyLoadState({
      visibleCount: LAZY_LOAD_INCREMENT,
      hasMore: true,
    });

    const isShowingAllYears = selectedYear === "yearAll";
    const displayStyle = isShowingAllYears ? "block" : "none";

    setTimeout(() => {
      if (isShowingAllYears) {
        showAllYearVersions(displayStyle);
      } else {
        showSpecificYearVersions(selectedYear, displayStyle);
      }
      updateLoadingState("main", false);
    }, LOADING_DELAY);
  };

  const fetchConnectivityOptions = async () => {
    try {
      const { data: options } = await axios.get(
        `${CONFIG.API_URL}vehicles/connectivity/type`,
        tokenHeaders,
      );
      setConnectivityOptions(options);
    } catch (error) {
      console.error("Failed to fetch connectivity options:", error);
    }
  };

  useEffect(() => {
    fetchConnectivityOptions();
  }, []);

  useEffect(() => {
    if (data && data !== vehicleData) {
      setVehicleData({
        id: data.id || 0,
        name: data.name || "",
        teaserMediaId: data.teaserMediaId || "",
        extension: data.extension || "",
        backgroundColor: data.backgroundColor || "",
        versions: data.versions || [],
      });
    }
  }, [data]);

  useEffect(() => {
    const versionsLength = vehicleData?.versions
      ? vehicleData.versions.length
      : 0;
    updateModelStates({
      childrenLength: versionsLength,
      modelLength: versionsLength,
    });

    if (vehicleData?.versions && vehicleData.versions.length > 0) {
      const uniqueYears = vehicleData.versions.reduce(
        (years: string[], version) => {
          const year = version.year.toString();
          if (!years.includes(year)) {
            years.push(year);
          }
          return years;
        },
        [],
      );

      updateYearFilter({
        availableYears: [...INITIAL_YEARS, ...uniqueYears],
      });
    } else {
      updateYearFilter({
        availableYears: INITIAL_YEARS,
      });
    }
  }, [vehicleData?.versions]);

  const { control, setValue, getValues, unregister } = useFormContext();
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: "versions",
  });

  // Função para contar elementos visíveis (não ocultos pelo filtro)
  const getVisibleItemsCount = useCallback(() => {
    if (!yearFilter.selectedYear || yearFilter.selectedYear === "yearAll") {
      return fields.length;
    }

    const selectedYear = yearFilter.selectedYear.replace("year", "");

    let visibleCount = 0;
    for (let i = 0; i < fields.length; i++) {
      const vehicleVersion = vehicleData.versions?.[i];
      const year = vehicleVersion?.year;

      if (year && year.toString() === selectedYear) {
        visibleCount++;
      }
    }

    return visibleCount;
  }, [yearFilter.selectedYear, fields.length, vehicleData.versions]);

  const loadMoreItems = useCallback(() => {
    const totalVisibleItems = getVisibleItemsCount();

    if (loadingStates.lazyLoading || !lazyLoadState.hasMore) {
      return;
    }

    const maxItems =
      !yearFilter.selectedYear || yearFilter.selectedYear === "yearAll"
        ? fields.length
        : totalVisibleItems;

    updateLoadingState("lazyLoading", true);

    setTimeout(() => {
      const newVisibleCount = lazyLoadState.visibleCount + LAZY_LOAD_INCREMENT;
      const finalVisibleCount = Math.min(newVisibleCount, maxItems);
      const hasMoreItems = finalVisibleCount < maxItems;

      updateLazyLoadState({
        visibleCount: finalVisibleCount,
        hasMore: hasMoreItems,
      });

      updateLoadingState("lazyLoading", false);
    }, 300);
  }, [
    getVisibleItemsCount,
    lazyLoadState.visibleCount,
    lazyLoadState.hasMore,
    loadingStates.lazyLoading,
    yearFilter.selectedYear,
    fields.length,
  ]);

  useEffect(() => {
    if (!lazyLoadState.hasMore || loadingStates.lazyLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          lazyLoadState.hasMore &&
          !loadingStates.lazyLoading
        ) {
          loadMoreItems();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px 0px 50px 0px",
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
      observer.disconnect();
    };
  }, [loadMoreItems, lazyLoadState.hasMore, loadingStates.lazyLoading]);

  useEffect(() => {
    const totalVisible = getVisibleItemsCount();

    updateLazyLoadState({
      visibleCount: Math.min(LAZY_LOAD_INCREMENT, totalVisible),
      hasMore: totalVisible > LAZY_LOAD_INCREMENT,
    });
  }, [yearFilter.selectedYear, getVisibleItemsCount]);

  const removeVersionFromData = (versionIndex: number) => {
    const updatedVersions = vehicleData.versions.filter(
      (_, index) => index !== versionIndex,
    );
    setVehicleData({
      ...vehicleData,
      versions: updatedVersions,
    });
  };

  const addVersionToData = () => {
    const newVersion = createNewVersion();
    const currentVersions = vehicleData.versions || [];
    setVehicleData({
      ...vehicleData,
      versions: [newVersion, ...currentVersions],
    });
  };

  const handleConnectivityChange = (
    versionIndex: number,
    connectivityValue: string,
  ) => {
    const updatedVersions = vehicleData.versions
      ? [...vehicleData.versions]
      : [];

    if (!updatedVersions[versionIndex]) {
      updatedVersions[versionIndex] = createNewVersion();
    }

    const selectedOption = connectivityOptions.find(
      (opt: IConnectivityOption) => opt.connectivity === connectivityValue
    );

    const hasExtraInfos =
      selectedOption?.connectivitiesInfos &&
      selectedOption.connectivitiesInfos.length > 0;

    const isOttoAllowed = connectivityValue === "Patagônia" || connectivityValue === "Carnet";
    
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      connectivity: connectivityValue,
      hasOtto: isOttoAllowed ? updatedVersions[versionIndex].hasOtto : false,
      isCarNetEnabled: connectivityValue === "Carnet",
      isPatagoniaEnabled: connectivityValue === "Patagônia",
      vwPlay: connectivityValue === "VwPlay" ? "true" : updatedVersions[versionIndex].vwPlay,      
      connectivityExtraInfo: hasExtraInfos
        ? updatedVersions[versionIndex].connectivityExtraInfo
        : undefined,
    };

    setVehicleData({
      ...vehicleData,
      versions: updatedVersions,
    });

      if (setValue && typeof setValue === "function") {
        setValue(`versions[${versionIndex}].connectivity`, connectivityValue);

        if (!isOttoAllowed) {
          setValue(`versions[${versionIndex}].hasOtto`, false, { shouldDirty: true });
        }

        setValue(`versions[${versionIndex}].isCarNetEnabled`, connectivityValue === "Carnet", { shouldDirty: true });
        setValue(`versions[${versionIndex}].isPatagoniaEnabled`, connectivityValue === "Patagônia", { shouldDirty: true });
  
        if (connectivityValue === "VwPlay") {
          setValue(`versions[${versionIndex}].vwPlay`, "true", { shouldDirty: true });
        }
      
        setValue(
          `versions.${versionIndex}.hasConnectivityExtraInfo`,
          hasExtraInfos,
          { shouldDirty: true, shouldValidate: false }
        );

        setValue(`versions.${versionIndex}.connectivityExtraInfo`, null, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    };

  const handlecConnectivityExtraInfosChange = (
    versionIndex: number,
    connectivityExtraInfosValue?: { id?: number; info?: string },
  ) => {
    const updatedVersions = vehicleData.versions
      ? [...vehicleData.versions]
      : [];

    if (!updatedVersions[versionIndex]) {
      updatedVersions[versionIndex] = createNewVersion();
    }
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      connectivityExtraInfo: connectivityExtraInfosValue,
    };
    setVehicleData({
      ...vehicleData,
      versions: updatedVersions,
    });

    if (!setValue || typeof setValue !== "function") {
      console.log('setValue não existe, ignorando setValue');
      return;
    }

    if (connectivityExtraInfosValue?.id) {
      setValue(
        `versions[${versionIndex}].connectivityExtraInfo.id`,
        connectivityExtraInfosValue.id
      );
      setValue(
        `versions[${versionIndex}].connectivityExtraInfo.info`,
        connectivityExtraInfosValue.info
      );
    } else {
      console.log('Setting connectivityExtraInfo to null');
      setValue(`versions[${versionIndex}].connectivityExtraInfo.id`, null);
      setValue(`versions[${versionIndex}].connectivityExtraInfo.info`, null);
    }
    console.log('vehicleData after setValue', vehicleData);
  };

  const handleDeleteConfirmation = () => {
    const { targetVersionIndex } = deleteStates;
    if (targetVersionIndex === undefined) return;

    if (
      modelStates.newVersionCount > 0 &&
      modelStates.newVersionCount >= targetVersionIndex
    ) {
      const newCount = modelStates.newVersionCount - 1;
      updateModelStates({ newVersionCount: newCount });

      if (newCount === 0) {
        updateYearFilter({ isEnabled: true });
        updateModelStates({ isNewModel: false });
      }
    } else {
      const yearToRemove = vehicleData.versions?.[targetVersionIndex]?.year;
      if (yearToRemove) {
        const updatedYears = yearFilter.availableYears.filter(
          (year) => year.toString() !== yearToRemove.toString(),
        );
        updateYearFilter({ availableYears: updatedYears });
        handleYearSelection("yearAll");
      }
    }

    removeVersionFromData(targetVersionIndex);
    remove(targetVersionIndex);
    updateModelStates({
      childrenLength: modelStates.childrenLength - 1,
    });

    if (targetVersionIndex + 1 <= modelStates.modelLength) {
      updateModelStates({
        modelLength: modelStates.modelLength - 1,
      });
    }

    updateDeleteStates({
      showConfirmDialog: false,
      targetVersionIndex: undefined,
    });
  };

  const handleCopyToEnvironment = async () => {
    updateLoadingState("copyEnvironment", true);
    try {
      await axios.post(
        `${CONFIG.API_URL}vehicles/integration/modelVersion/send?email=${user.email}&countryCodeOrigin=${countryCode}&countryCodeDestination=${copyStates.country}`,
        {
          environment: copyStates.environment,
          modelId: vehicleData.id,
          versionsId: copyStates.selectedVersions,
        },
        tokenHeaders,
      );
    } catch (error) {
      console.error("Failed to copy to environment:", error);
    } finally {
      updateLoadingState("copyEnvironment", false);
    }
  };

  const handleAddNewVersion = () => {
    updateLoadingState("addButton", true);
    updateYearFilter({ isEnabled: false });
    updateModelStates({
      isNewModel: true,
      newVersionCount: modelStates.newVersionCount + 1,
    });

    setTimeout(() => {
      prepend(createNewVersion());
      addVersionToData();

      updateLoadingState("addButton", false);
      updateModelStates({
        childrenLength: modelStates.childrenLength + 1,
      });

      updateLazyLoadState({
        visibleCount: Math.min(
          LAZY_LOAD_INCREMENT,
          modelStates.childrenLength + 1,
        ),
        hasMore: modelStates.childrenLength + 1 > LAZY_LOAD_INCREMENT,
      });
    }, LOADING_DELAY);
  };

  return (
    <Box>
      {editVehicle && (
        <Box>
          <Grid
            hidden={
              CONFIG.ENVIRONMENT !== "UAT" && CONFIG.ENVIRONMENT !== "STAGING"
            }
          >
            <Divider />
            <Grid container sx={{ paddingTop: "16px", paddingBottom: "16px" }}>
              <Grid item xs={4}>
                <FormGroup sx={{ marginBottom: "1rem" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={copyStates.isEnabled}
                        onChange={() =>
                          updateCopyStates({ isEnabled: !copyStates.isEnabled })
                        }
                      />
                    }
                    label={t("models.copyAnotherEnvironment")}
                  />
                </FormGroup>
              </Grid>
              <Grid
                item
                xs={4}
                hidden={!copyStates.isEnabled}
                sx={{ paddingRight: "16px" }}
              >
                {t("models.selectDestinationEnvironment")}:
              </Grid>
              <Grid item xs={2} hidden={!copyStates.isEnabled}>
                <Select
                  displayEmpty
                  placeholder="Select"
                  sx={{ height: 35, minWidth: "208px" }}
                  onChange={handleEnvironmentChange}
                  value={copyStates.environment || ""}
                >
                  <MenuItem value="">
                    <em>{t("models.selectEnvironment")}</em>
                  </MenuItem>
                  <MenuItem value="PRODUCTION">
                    {t("models.production")}
                  </MenuItem>
                  <MenuItem value="STAGING">
                    {t("models.preProduction")}
                  </MenuItem>
                  <MenuItem value="UAT">{t("models.uat")}</MenuItem>
                  <MenuItem value="DEVELOPMENT">
                    {t("models.development")}
                  </MenuItem>
                </Select>
              </Grid>
              <Grid
                item
                xs={4}
                hidden={!copyStates.isEnabled}
                sx={{ paddingBottom: "16px" }}
              >
                <Select
                  displayEmpty
                  placeholder="Select"
                  sx={{ height: 35, minWidth: "208px" }}
                  onChange={(event: any) => {
                    updateCopyStates({ country: event.target.value });
                  }}
                  value={copyStates.country || ""}
                >
                  <MenuItem value="">
                    <em>{t("models.selectEnvironment")}</em>
                  </MenuItem>
                  {countries.map((aux: any) => {
                    return (
                      <MenuItem key={aux.countryCode} value={aux.countryCode}>
                        {aux.country}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
              <Button
                sx={{
                  display:
                    copyStates.isEnabled === false ? "none" : "inline-flex",
                }}
                variant="contained"
                type="button"
                fullWidth
                onClick={handleCopyToEnvironment}
                disabled={
                  !(
                    copyStates.selectedVersions.length !== 0 &&
                    copyStates.environment !== "" &&
                    copyStates.country !== ""
                  )
                }
              >
                {t("models.makeCopy")}
              </Button>
              <Grid xs={12} sx={{ textAlign: "center" }}>
                {loadingStates.copyEnvironment && <CircularProgress />}
              </Grid>
            </Grid>
          </Grid>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {loadingStates.main && <CircularProgress />}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                gap: 1,
              }}
            >
              <p>{t("models.selectModelYear")}:</p>
              <Select
                displayEmpty
                labelId="colorSelect"
                id="colorSelect"
                placeholder="Select"
                sx={{ height: 35, width: 180 }}
                onChange={(e) => {
                  updateLoadingState("main", true);
                  handleYearSelection(e.target.value);
                }}
                value={yearFilter.selectedYear || ""}
                disabled={!yearFilter.isEnabled}
              >
                <MenuItem disabled value="">
                  <em>{t("models.selectYear")}</em>
                </MenuItem>
                {[...yearFilter.availableYears]
                  .sort((a: string, b: string) => a.localeCompare(b))
                  .reverse()
                  .map((item: string, index: number) => {
                    return (
                      <MenuItem
                        key={`year-${item}-${index}`}
                        value={`year${item}`}
                      >
                        {item === "All" ? "Todos" : item}
                      </MenuItem>
                    );
                  })}
              </Select>
            </Box>
          </Box>
          {!yearFilter.isEnabled && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                marginBottom: 2,
                color: "var(--gray-300)",
                fontSize: "14px",
              }}
            >
              {t("models.disabledSelectYear")}
            </Box>
          )}
        </Box>
      )}

      {yearFilter.noResultsFound && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            color: "var(--red-100)",
            marginBottom: 2,
            fontSize: "14px",
          }}
        >
          {t("models.yearNotFound")}
        </Box>
      )}
      <Box
        sx={{
          marginBottom: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
          }}
        >
          <Button
            variant="contained"
            type="button"
            fullWidth
            onClick={handleAddNewVersion}
          >
            {t("models.addVersion")}
          </Button>
        </Box>

        {loadingStates.addButton && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginTop: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      {(() => {
        let itemsToRender;

        if (!yearFilter.selectedYear || yearFilter.selectedYear === "yearAll") {
          itemsToRender = lazyLoadState.visibleCount;
        } else {
          const visibleItemsCount = getVisibleItemsCount();
          if (visibleItemsCount <= lazyLoadState.visibleCount) {
            itemsToRender = fields.length;
          } else {
            itemsToRender = Math.min(
              fields.length,
              lazyLoadState.visibleCount * 3,
            );
          }
        }

        return fields
          .slice(0, itemsToRender)
          .map((child: any, childIndex: number) => {
            const vehicleVersion = vehicleData.versions?.[childIndex];
            const year = vehicleVersion?.year;
            const versionClass = year ? `year${year}` : "";

            return (
              <div key={child.id} className={versionClass}>
                <VersionVehicle
                  child={child}
                  childIndex={childIndex}
                  errors={errors}
                  updateDeleteStates={updateDeleteStates}
                  handleConnectivityChange={handleConnectivityChange}
                  handlecConnectivityExtraInfosChange={handlecConnectivityExtraInfosChange}
                  modelStates={modelStates}
                  showErrorMessage={showErrorMessage}
                  connectivityOptions={connectivityOptions}
                  vehicleData={vehicleData}
                  copyStates={copyStates}
                  updateCopyStates={updateCopyStates}
                  editVehicle={editVehicle}
                  setModelStates={setModelStates}
                  yearFilter={yearFilter}
                />
              </div>
            );
          });
      })()}

      {lazyLoadState.hasMore && (
        <Box
          ref={loadMoreRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: 2,
            minHeight: 60,
          }}
        >
          {loadingStates.lazyLoading && <CircularProgress size={24} />}
        </Box>
      )}

      {deleteStates.showConfirmDialog && (
        <ModalConfirmation
          openDelete={(open: boolean) =>
            updateDeleteStates({ showConfirmDialog: open })
          }
          confirmDelete={handleDeleteConfirmation}
          title={`${t("button.Confirm delete")} ${t("models.versionRemove")} ${deleteStates.targetVersionIndex !== undefined &&
            vehicleData.versions[deleteStates.targetVersionIndex]?.name
            ? vehicleData.versions[deleteStates.targetVersionIndex].name
            : ""
            }`}
        />
      )}
      {loadingStates.addButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box ref={addButtonRef} />
    </Box>
  );
}